import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { CmAuthService } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

const enum UIActionTypes {
	SET_VIEW_VISIBILITY = 'Set the view visibility',
}

interface UIAction {
	readonly type: UIActionTypes;
	readonly payload: boolean;
}

class UIState {
	hideUI: boolean = true;
}

const reduce = (state: UIState, action: UIAction) => {
	switch (action.type) {
		case UIActionTypes.SET_VIEW_VISIBILITY:
			return {
				...state,
				hideUI: action.payload,
			};
		default:
			return state;
	}
};

@Injectable({
	providedIn: 'root',
})
export class CmUIService extends Unsubscribable implements Resolve<void> {
	private state = new UIState();
	private dispatch = new BehaviorSubject<UIState>(this.state);

	lastResolvedRoute: string;

	hideUI$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.hideUI),
		distinctUntilChanged()
	);

	constructor(private auth: CmAuthService, private router: Router) {
		super();
		this.init();
	}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		this.lastResolvedRoute = state.url;
	}

	private init() {
		const auth$ = this.auth.token$.pipe(map(Boolean));
		const routeChange$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

		combineLatest([auth$, routeChange$])
			.pipe(takeUntil(this.unsub))
			.subscribe(([auth, route]: [boolean, NavigationEnd]) => {
				if (!auth) {
					this.updateUIVisibility(true);
					return;
				}

				if (this.lastResolvedRoute && this.lastResolvedRoute === route.urlAfterRedirects)
					this.updateUIVisibility(true);
				else this.updateUIVisibility(false);
			});
	}

	private updateUIVisibility(hideUI: boolean) {
		const type = UIActionTypes.SET_VIEW_VISIBILITY;
		const action = { payload: hideUI, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}
}
